import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { API, getAuthHeader } from './api';

export function AdvisorsManagement() {
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAdvisor, setEditingAdvisor] = useState(null);
  const [formData, setFormData] = useState({
    name: '', title: '', expertise: '', bio: '', photo_url: '', linkedin_url: '', email: ''
  });

  useEffect(() => { fetchAdvisors(); }, []);

  const fetchAdvisors = async () => {
    try { const response = await axios.get(`${API}/advisors`); setAdvisors(response.data); }
    catch { /* silent */ }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAdvisor) {
        await axios.put(`${API}/advisors/${editingAdvisor.id}`, formData, { headers: getAuthHeader() });
        toast.success('Advisor updated');
      } else {
        await axios.post(`${API}/advisors`, formData, { headers: getAuthHeader() });
        toast.success('Advisor created');
      }
      setIsModalOpen(false); fetchAdvisors();
    } catch (error) { toast.error('Failed to save advisor'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this advisor?')) return;
    try { await axios.delete(`${API}/advisors/${id}`, { headers: getAuthHeader() }); toast.success('Advisor deleted'); fetchAdvisors(); }
    catch (error) { toast.error('Failed to delete'); }
  };

  const openModal = (advisor = null) => {
    if (advisor) { setEditingAdvisor(advisor); setFormData(advisor); }
    else { setEditingAdvisor(null); setFormData({ name: '', title: '', expertise: '', bio: '', photo_url: '', linkedin_url: '', email: '' }); }
    setIsModalOpen(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-[#231F20] font-['Bricolage_Grotesque']">Manage Advisors</h1>
        <Button onClick={() => openModal()} className="bg-[#075FA3] hover:bg-[#0077B6]" data-testid="add-advisor-btn"><Plus className="w-4 h-4 mr-2" /> Add Advisor</Button>
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Title</TableHead><TableHead>Expertise</TableHead><TableHead>Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {advisors.map((advisor) => (
              <TableRow key={advisor.id}>
                <TableCell className="font-medium">{advisor.name}</TableCell>
                <TableCell>{advisor.title}</TableCell>
                <TableCell><Badge variant="outline">{advisor.expertise}</Badge></TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => openModal(advisor)}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(advisor.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {advisors.length === 0 && !loading && (
              <TableRow><TableCell colSpan={4} className="text-center text-[#58595B] py-8">No advisors yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingAdvisor ? 'Edit Advisor' : 'Add Advisor'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Name *</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required /></div>
              <div><Label>Title *</Label><Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required /></div>
            </div>
            <div><Label>Expertise *</Label><Input value={formData.expertise} onChange={(e) => setFormData({ ...formData, expertise: e.target.value })} placeholder="e.g., Product Strategy, Fundraising" required /></div>
            <div><Label>Bio *</Label><Textarea value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} required /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Photo URL</Label><Input value={formData.photo_url} onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })} /></div>
              <div><Label>LinkedIn URL</Label><Input value={formData.linkedin_url} onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })} /></div>
            </div>
            <div><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></div>
            <Button type="submit" className="w-full bg-[#075FA3]">{editingAdvisor ? 'Update' : 'Create'} Advisor</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
